/**
 * Always-visible consent indicator (AGENTS.md §4 — consent is a UI
 * primitive, not modal-only legalese). Shown to both operator and
 * field.
 *
 * Recording state gets a special treatment: when active the REC pill
 * renders as a pulsing red badge with HH:MM:SS elapsed time so the
 * field user can see at a glance that the session is being recorded.
 * The other consent items keep their compact text-only style — REC
 * being active is the most safety-critical signal in the bar.
 */
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { useConsentStore, type ConsentKind } from "@/lib/consent";
import { useRecordingStore } from "@/lib/recording";

const items: { kind: ConsentKind; labelKey: string }[] = [
  { kind: "audio", labelKey: "ui.consentHud.mic" },
  { kind: "video", labelKey: "ui.consentHud.cam" },
  // recording handled separately below — needs the pulsing badge.
  { kind: "snapshot", labelKey: "ui.consentHud.snap" },
  { kind: "geolocation", labelKey: "ui.consentHud.gps" },
];

export function ConsentHud() {
  const { t } = useTranslation();
  const consents = useConsentStore((s) => s.state);
  return (
    <div
      role="status"
      aria-live="polite"
      className="pointer-events-none fixed left-1/2 top-3 z-40 -translate-x-1/2 rounded-full bg-nb-bg/80 px-3 py-1 text-xs backdrop-blur"
    >
      <ul className="flex items-center gap-3">
        {items.map(({ kind, labelKey }) => {
          const v = consents[kind];
          const colour =
            v === "granted"
              ? "text-cvp-ok"
              : v === "denied" || v === "revoked"
                ? "text-cvp-mute line-through"
                : "text-cvp-mute";
          return (
            <li key={kind} className={colour}>
              {t(labelKey)}
            </li>
          );
        })}
        <li>
          <RecordingItem recordingConsent={consents.recording} />
        </li>
      </ul>
    </div>
  );
}


/** REC pill — compact text when inactive, pulsing red badge with
 *  elapsed time when recording. The badge IS the indicator the field
 *  (and operator) needs to spot at a glance during an inspection.
 *  Source of truth is `useRecordingStore`, populated by either
 *  RecordingControl (operator-initiated) or FieldSystemHandler
 *  (broadcast from operator via the bus). */
function RecordingItem({
  recordingConsent,
}: {
  recordingConsent: "granted" | "denied" | "revoked" | null;
}) {
  const { t } = useTranslation();
  const active = useRecordingStore((s) => s.active);
  const paused = useRecordingStore((s) => s.paused);
  const startedAt = useRecordingStore((s) => s.startedAt);
  if (active && paused) {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-500 px-2 py-0.5 text-[11px] font-semibold text-black">
        <span className="h-1.5 w-1.5 rounded-full bg-black" aria-hidden />
        {t("ui.consentHud.paused")}
        {startedAt !== null && <Elapsed startedAt={startedAt} />}
      </span>
    );
  }
  if (active) {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-full bg-cvp-danger px-2 py-0.5 text-[11px] font-semibold text-white">
        <span
          className="h-1.5 w-1.5 animate-pulse rounded-full bg-white"
          aria-hidden
        />
        {t("ui.consentHud.rec")}
        {startedAt !== null && <Elapsed startedAt={startedAt} />}
      </span>
    );
  }
  const colour =
    recordingConsent === "denied" || recordingConsent === "revoked"
      ? "text-cvp-mute line-through"
      : "text-cvp-mute";
  return <span className={colour}>{t("ui.consentHud.rec")}</span>;
}


function Elapsed({ startedAt }: { startedAt: number }) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, []);
  const sec = Math.max(0, Math.floor((now - startedAt) / 1000));
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  const s = sec % 60;
  return (
    <span className="font-mono tabular-nums">
      {pad(h)}:{pad(m)}:{pad(s)}
    </span>
  );
}

function pad(n: number): string {
  return n.toString().padStart(2, "0");
}